import { styled } from '@mui/material';
import { useBrandSummaryMetrics } from 'src/queries/useBrandSummaryMetrics';
import { cssResetStyles, DisplayCard, FrontTooltip, Skeleton } from 'src/ui-components';
import { Numbers } from 'src/ui-components/helpers/formatters';

import { TrendIcon } from '../../../../../ui-components/icons';
import type { IBlockData } from '../../../helpers/types';
import { useGetValues } from '../../../hooks/useGetValues';

export const BlockData = ({
  title,
  type,
  field,
  change_field,
  tooltipTitle,
  decimal,
}: IBlockData) => {
  const { isLoading } = useBrandSummaryMetrics();
  const { value, percentValue, previousDates, previousValue, positiveDynamics } = useGetValues({
    type,
    field,
    change_field,
    decimal,
  });

  return (
    <FrontTooltip
      disabled={isLoading}
      placement={'right'}
      arrow
      title={
        <TooltipContainer>
          <TooltipRow>
            <TooltipText>{tooltipTitle || title}</TooltipText>
          </TooltipRow>
          <TooltipRow>
            <TooltipText>
              {previousDates} {previousValue}
            </TooltipText>
          </TooltipRow>
        </TooltipContainer>
      }
    >
      <Wrapper>
        <Title>{title}</Title>
        {isLoading ? <Skeleton h={'32px'} /> : <Value>{value}</Value>}

        <Percent>
          {isLoading ? (
            <Skeleton h={'20px'} />
          ) : (
            <>
              <PercentText $positive={positiveDynamics}>{percentValue}</PercentText>
              {positiveDynamics !== 'neutral' && <IconStyled $positive={positiveDynamics} />}
            </>
          )}
        </Percent>
      </Wrapper>
    </FrontTooltip>
  );
};

const Wrapper = styled(DisplayCard.Content)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const TooltipContainer = styled('div')`
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  max-width: 400px;
`;

const TooltipRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;
  width: 100%;
`;

const TooltipText = styled('span')`
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const Title = styled('div')`
  overflow: hidden;
  color: #5f676b;
  text-overflow: ellipsis;
  font-family: ${({ theme }) => theme.fonts.telkaExtended};
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  text-transform: uppercase;
`;

const Value = styled('p')`
  ${cssResetStyles};
  color: #121517;
  font-family: ${({ theme }) => theme.fonts.telkaExtended};
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 32px;
`;

const Percent = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 50%;
`;

const PercentText = styled('div')<{ $positive: Numbers.DynamicType }>`
  overflow: hidden;
  color: ${({ $positive }) => Numbers.dynamicsColors[$positive]};
  text-overflow: ellipsis;

  font-family: ${({ theme }) => theme.fonts.telka};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`;

const IconStyled = styled(TrendIcon)<{ $positive: Numbers.DynamicType }>`
  ${cssResetStyles};
  width: 16px;
  height: 16px;
  color: ${({ $positive }) => Numbers.dynamicsColors[$positive]};
  transform: rotate(${({ $positive }) => ($positive !== 'negative' ? 0 : '90deg')});
`;
